/**
 * Hilfsfunktionen für die Darstellung des Stammdaten-Änderungsprotokolls
 * (ma_stammdaten_protokoll). Wird im Frontend (neoChecker) und im Backend
 * (Bericht "Stammdaten Änderungen") verwendet, daher ohne globalen State:
 * Unternehmensobjekt und Beschäftigungsarten werden als Parameter übergeben.
 */

/* ── Feld-Labels (lesbar, deutsch) — nach Bedarf erweitern ── */
export const FIELD_LABELS = {
  Uebersicht: "Übersicht",
  KurzfristigTage: "Kurzfristig (Tage)",
  Beschaeftigung: "Beschäftigung",
  Beschaeftigungsart: "Beschäftigungsart",
  GutzeitVorwert: "Gutzeit-Vorwert",
  Verguetung: "Vergütung",
  QualifikationID: "Qualifikation",
  Betriebsstaette: "Betriebsstätte",
  NettoBeAbzuege: "Netto-Be-/Abzüge",
  Basisgrundlohn: "Basisgrundlohn",
  Entlohnung: "Entlohnungsform",
  Zielbrutto: "Zielbrutto",
  Eintrittsdatum: "Eintrittsdatum",
  BefristetBis: "Befristet bis",
  ProbezeitBis: "Probezeit bis",
  Steuer: "Steuer",
  Monatsfreibetrag: "Monatsfreibetrag",
  Personalien: "Personalien",
  Geburtsdatum: "Geburtsdatum",
  PrivateKrankenkasse: "Private Krankenkasse",
  PrivatVersichert: "Privat versichert",
  Beitrag: "Beitrag",
  BeitragBasistarif: "Beitrag (Basistarif)",
  FreiwilligeGesetzlicheKrankenversicherung: "Freiw. ges. Krankenversicherung",
  FreiwilligeGesetzlichePflegeversicherung: "Freiw. ges. Pflegeversicherung",
  FreiwilligVersichert: "Freiwillig versichert",
};

/* ── Systemgepflegte Felder ausblenden: Uebersicht wird automatisch
      berechnet und ist keine manuelle Änderung — bis auf diese Felder ── */
const UEBERSICHT_SICHTBAR = ["Notizen"];
// Lohnarten-Detailfelder, die beim Umsortieren des Arrays nur Diff-Spam
// erzeugen — ausgeblendet bleibt erkennbar, dass es nur ein Resort war
const LOHNARTEN_AUSGEBLENDET = ["Steuer", "Sozialversicherung", "LohnartTyp", "FiBuKonto", "InExport", "Prozent", "Rechengroesse"];
export function istSichtbar(diff) {
  const [root, child] = diff.path;
  const leaf = diff.path[diff.path.length - 1];
  if (root === "Uebersicht" && !UEBERSICHT_SICHTBAR.includes(child)) return false;
  // _id-"Änderungen" in Vergütungen/Lohnarten entstehen nur durch Umsortierung
  // oder Entfernen anderer Einträge, nicht durch manuelles Bearbeiten
  if ((diff.path.includes("Verguetung") || diff.path.includes("Lohnarten")) && leaf === "_id") return false;
  if (diff.path.includes("Lohnarten") && LOHNARTEN_AUSGEBLENDET.includes(leaf)) return false;
  // Sofortmelder-Historie wird vom System geschrieben, keine manuelle Änderung
  if (diff.path.includes("SofortmelderHistorie")) return false;
  // "leer → leer": nur die Art des Leerwerts hat sich geändert (z.B. undefined -> ""), keine echte Änderung
  if (isEmpty(diff.oldValue) && isEmpty(diff.value)) return false;
  return true;
}

// Lohnarten/Netto-Be-/Abzüge: "Regelmaessig" ist true oder ein Datum (= einmalig in diesem Monat),
// verständlicher ist die Anzeige als "Einmalig" mit invertiertem Wahrheitswert bzw. dem Datum
function istRegelmaessigFeld(path) {
  return path[path.length - 1] === "Regelmaessig" && (path.includes("Lohnarten") || path.includes("NettoBeAbzuege"));
}

/* ── Helfer ── */
function humanize(key) {
  return String(key).replace(/([a-z0-9])([A-Z])/g, "$1 $2").replace(/_/g, " ");
}
export function labelFor(key) { return FIELD_LABELS[key] || humanize(key); }

// ID-Pfadsegmente aus dem key-basierten Diff (arrayToObjectById),
// bei Duplikaten hängt das Backend "_<index>" an
const OBJECT_ID_SEGMENT = /^[0-9a-f]{24}(_\d+)?$/i;

function resolvePathId(arrayKey, id, unternehmen) {
  if (!unternehmen) return null;
  const cleanId = id.split("_")[0];
  const findById = (arr) => (arr || []).find(e => String(e._id) === cleanId);
  switch (arrayKey) {
    case "Lohnarten":
    case "NettoBeAbzuege":
      return findById(unternehmen[arrayKey])?.Bezeichnung || null;
    case "Qualifikationen":
      return findById(unternehmen.Qualifikationen)?.BezeichnungNeutral || null;
    case "Pausenmodelle":
      return findById(unternehmen.Pausenmodelle)?.Bezeichnung || null;
    case "Bereiche":
      return findById(unternehmen.Bereiche)?.Bereich || null;
    case "Verzeichnisse":
      return findById(unternehmen.Verzeichnisse)?.VerzeichnisName || null;
    case "Posten":
      return findById((unternehmen.Betriebsstaette || []).flatMap(bs => bs.Posten || []))?.Posten || null;
    case "Einsatzorte":
      return findById((unternehmen.Betriebsstaette || []).flatMap(bs => bs.Einsatzorte || []))?.Bezeichnung || null;
    default:
      return null;
  }
}

/**
 * Baut aus dem Diff-Pfad lesbare Pfadsegmente ({ label, index }).
 * @param {array} path Diff-Pfad
 * @param {object} unternehmen Unternehmensobjekt zum Auflösen von IDs
 */
export function buildCrumbs(path, unternehmen) {
  const segs = [];
  let lastKey = null;
  for (const item of path) {
    if (typeof item === "number") {
      // Index 0 nicht anzeigen ("Beschäftigung" statt "Beschäftigung 1"),
      // bei Vergütung aber immer, damit erkennbar ist, welche geändert wurde
      if (segs.length && (item > 0 || lastKey === "Verguetung")) segs[segs.length - 1].index = item + 1;
    } else if (OBJECT_ID_SEGMENT.test(item)) {
      // ID statt Index: wenn möglich in einen Namen auflösen, sonst ausblenden
      if (segs.length) segs[segs.length - 1].index = resolvePathId(lastKey, item, unternehmen);
    } else {
      lastKey = item;
      const label = item === "Regelmaessig" && istRegelmaessigFeld(path) ? "Einmalig" : labelFor(item);
      segs.push({ label, index: null });
    }
  }
  return segs;
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
function isEmpty(v) { return v === null || v === undefined || v === ""; }
export function esc(s) {
  return String(s).replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
}
export function fmtValue(v) {
  if (isEmpty(v)) return { kind: "empty", text: "leer" };
  if (typeof v === "boolean") return { kind: "bool", on: v, text: v ? "Ja" : "Nein" };
  if (typeof v === "string" && ISO_DATE.test(v)) {
    const [y, m, d] = v.split("-");
    return { kind: "text", text: `${d}.${m}.${y}` };
  }
  if (typeof v === "number") return { kind: "text", text: v.toLocaleString("de-DE") };
  return { kind: "text", text: String(v) };
}

/**
 * IDs in lesbare Bezeichnungen auflösen (Pausenmodell aus dem
 * Unternehmen, Einsatzorte/Posten aus den Betriebsstätten)
 * @param {array} path Diff-Pfad
 * @param {*} value alter oder neuer Wert
 * @param {object} unternehmen Unternehmensobjekt
 * @param {array} beschaeftigungsarten Beschäftigungsarten aus dem Basedata Provider
 */
export function resolveValue(path, value, unternehmen, beschaeftigungsarten) {
  if (isEmpty(value)) return value;
  // Regelmaessig true -> Einmalig "Nein", ein Datum wird direkt angezeigt
  if (istRegelmaessigFeld(path)) {
    return typeof value === "boolean" ? !value : value;
  }
  // Beschäftigungsart ist eine ID (BeschID) aus dem Basedata-Provider
  if (path.includes("Beschaeftigungsart")) {
    const art = (beschaeftigungsarten || []).find(b => String(b.BeschID) === String(value));
    return art ? art.BeschaeftigungsartKurz : value;
  }
  if (!unternehmen) return value;
  if (path.includes("Pausenmodell") || path.includes("PausenmodellStandard")) {
    const modell = (unternehmen.Pausenmodelle || []).find(p => String(p._id) === String(value));
    return modell ? modell.Bezeichnung : value;
  }
  if (path.includes("Pausenmodelle") && typeof value === "object") {
    // ganzes Pausenmodell (hinzugefügt/entfernt)
    if (value.Bezeichnung) return value.Bezeichnung;
    // einzelner Pausenstandard-Eintrag
    if (value.Nach !== undefined) {
      return `nach ${value.Nach} · ${value.Dauer} Min. · ${value.Bezahlt ? "bezahlt" : "unbezahlt"}`;
    }
  }
  if (path.includes("Einsatzorte")) {
    const id = typeof value === "object" ? value.EinsatzortID : value;
    const ort = (unternehmen.Betriebsstaette || []).flatMap(bs => bs.Einsatzorte || [])
      .find(e => String(e._id) === String(id));
    if (ort) return ort.Bezeichnung;
    if (typeof value !== "object") return value;
    // sonst: generischer Objekt-Fallback unten
  }
  if (path.includes("Posten")) {
    const id = typeof value === "object" ? value.PostenID : value;
    const posten = (unternehmen.Betriebsstaette || []).flatMap(bs => bs.Posten || [])
      .find(p => String(p._id) === String(id));
    if (posten) return posten.Posten;
    if (typeof value !== "object") return value;
  }
  if (path.includes("QualifikationID")) {
    const quali = (unternehmen.Qualifikationen || []).find(q => String(q._id) === String(value));
    return quali ? quali.BezeichnungNeutral : value;
  }
  if (path.includes("Verguetung") && typeof value === "object") {
    // ganze Vergütung (hinzugefügt/entfernt): über die Qualifikation benennen
    const quali = (unternehmen.Qualifikationen || []).find(q => String(q._id) === String(value.QualifikationID));
    if (quali) return quali.BezeichnungNeutral;
  }
  if (path.includes("Sonderurlaub") && typeof value === "object") {
    // ganzer Sonderurlaub (hinzugefügt/entfernt): Tage und Notiz anzeigen
    const tage = value.SonderurlaubTage;
    const text = [isEmpty(tage) ? null : `${tage} ${tage === 1 ? "Tag" : "Tage"}`, value.Notiz].filter(v => !isEmpty(v)).join(" · ");
    return text || value;
  }
  if ((path.includes("Lohnarten") || path.includes("NettoBeAbzuege")) && typeof value === "object") {
    const text = [value.Lohnartennummer, value.Bezeichnung].filter(v => !isEmpty(v)).join(" · ");
    return text || value;
  }
  // generischer Fallback für ganze Objekte: erstes bekanntes
  // Bezeichnungsfeld anzeigen statt "[object Object]"
  if (typeof value === "object") {
    const name = [
      value.Bezeichnung, value.BezeichnungNeutral, value.VerzeichnisName,
      value.Bereich, value.Posten, value.BetriebsstaetteName,
      value.Betriebsnummer, value.EinsatzortID, value.PostenID,
    ].find(v => !isEmpty(v));
    if (name !== undefined) return name;
  }
  return value;
}
